import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { DatePicker } from './ui/date-picker';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { TimeField } from './ui/time-field';
import { CalendarClock, AlertTriangle } from 'lucide-react';

/**
 * Quick Reserve — create a soft hold on an asset. Desktop/dispatcher only.
 * Props: open, onOpenChange, api, presetEquipmentId, presetDate (YYYY-MM-DD), onCreated()
 */
export const QuickReserveDialog = ({ open, onOpenChange, api, basePath = '', presetEquipmentId, presetDate, onCreated }) => {
  const [equipment, setEquipment] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    equipment_id: '', start_date: '', end_date: '', start_time: '', end_time: '', customer_id: '', note: '',
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      equipment_id: presetEquipmentId || '',
      start_date: presetDate || new Date().toISOString().slice(0, 10),
      end_date: '', start_time: '', end_time: '', customer_id: '', note: '',
    });
    Promise.all([
      api.get(`${basePath}/equipment`).catch(() => ({ data: [] })),
      api.get(`${basePath}/customers`).catch(() => ({ data: [] })),
    ]).then(([eq, cu]) => {
      setEquipment(eq.data || []);
      setCustomers((cu.data || []).filter(c => c.is_active !== false));
    });
  }, [open, presetEquipmentId, presetDate, api, basePath]);

  const handleSave = async () => {
    if (!form.equipment_id) { toast.error('Select an asset'); return; }
    if (!form.start_date) { toast.error('Select a date'); return; }
    setSaving(true);
    try {
      const payload = {
        equipment_id: form.equipment_id,
        start_date: form.start_date,
        end_date: form.end_date || form.start_date,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        customer_id: form.customer_id || null,
        note: form.note || null,
      };
      const res = await api.post(`${basePath}/reservations`, payload);
      const warnings = res.data?.warnings || [];
      if (warnings.length > 0) {
        toast.warning(`Asset reserved — heads up: ${warnings.length} overlapping booking/hold on these dates.`);
      } else {
        toast.success('Asset reserved');
      }
      onOpenChange(false);
      onCreated && onCreated(res.data?.reservation);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reserve asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-reserve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <CalendarClock className="h-5 w-5 text-[#ffd108]" /> Quick Reserve
          </DialogTitle>
          <DialogDescription>Place a tentative soft hold on an asset. It warns on conflicts but won't block bookings.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Asset <span className="text-red-500">*</span></Label>
            <Select value={form.equipment_id} onValueChange={(v) => setForm(p => ({ ...p, equipment_id: v }))}>
              <SelectTrigger data-testid="qr-asset-select"><SelectValue placeholder="Select asset" /></SelectTrigger>
              <SelectContent>
                {equipment.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.asset_id} — {e.equipment_type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>From date <span className="text-red-500">*</span></Label>
              <DatePicker value={form.start_date} onChange={(d) => setForm(p => ({ ...p, start_date: d }))} className="w-full" testId="qr-start-date" />
            </div>
            <div>
              <Label>To date</Label>
              <DatePicker value={form.end_date} minDate={form.start_date} onChange={(d) => setForm(p => ({ ...p, end_date: d }))} className="w-full" testId="qr-end-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start time</Label>
              <TimeField value={form.start_time} onChange={(v) => setForm(p => ({ ...p, start_time: v }))} allowNone testId="qr-start-time" />
            </div>
            <div>
              <Label>End time</Label>
              <TimeField value={form.end_time} onChange={(v) => setForm(p => ({ ...p, end_time: v }))} allowNone testId="qr-end-time" />
            </div>
          </div>
          <div>
            <Label>Customer <span className="text-muted-foreground text-xs">(needed to convert to a job)</span></Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm(p => ({ ...p, customer_id: v }))}>
              <SelectTrigger data-testid="qr-customer-select"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note</Label>
            <Textarea value={form.note} onChange={(e) => setForm(p => ({ ...p, note: e.target.value }))} placeholder="e.g. Pencilled in for Smith — confirming Tuesday" rows={2} data-testid="qr-note" />
          </div>
          <p className="flex items-start gap-2 text-xs text-amber-500/90">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Reserving a combo asset holds all its member assets too.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-volt" onClick={handleSave} disabled={saving} data-testid="qr-submit-btn">
            {saving ? 'Reserving…' : 'Reserve Asset'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickReserveDialog;
